
import Link from '@/components/compat/Link';
import type { ElementType } from 'react';
import { useMemo, useState } from 'react';
import { ArrowLeft, Bug, ChevronDown, ExternalLink, Pencil, Sparkles, TrendingUp, Zap } from 'lucide-react';
import { AceNavIcon } from '@/components/help-center/aces/AceIcon';
import type { ChangelogSection, SectionType } from '@/lib/changelog/changelog-types';
import type { ChangelogProductId } from '@/lib/changelog/changelog-sources';
import { useChangelogSources } from '@/hooks/use-changelog-sources';
import { changelogVersionHref } from '@/components/painel/changelog/changelog-nav-config';
import { changelogPublicHref } from '@/components/painel/changelog/changelog-painel-utils';
import { ChangelogProductTabs } from '@/components/painel/changelog/changelog-product';
import { Button } from '@/components/ui/button';
import { painelSurfaceClass, painelPageBackButtonClass, painelGhostNavButtonClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import { ChangelogPageShell } from '@/components/painel/changelog/changelog-ui';

const sectionConfig: Record<
  SectionType,
  { color: string; bg: string; border: string; badgeBg: string; badgeText: string; icon: ElementType }
> = {
  new: {
    color: 'text-sky-200',
    bg: 'bg-sky-500/10',
    border: 'border-sky-400/25',
    badgeBg: 'bg-sky-500/15',
    badgeText: 'text-sky-100',
    icon: Sparkles,
  },
  powers: {
    color: 'text-violet-200',
    bg: 'bg-violet-500/10',
    border: 'border-violet-400/25',
    badgeBg: 'bg-violet-500/15',
    badgeText: 'text-violet-100',
    icon: Zap,
  },
  improvement: {
    color: 'text-emerald-200',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-400/25',
    badgeBg: 'bg-emerald-500/15',
    badgeText: 'text-emerald-100',
    icon: TrendingUp,
  },
  fix: {
    color: 'text-rose-200',
    bg: 'bg-rose-500/10',
    border: 'border-rose-400/25',
    badgeBg: 'bg-rose-500/15',
    badgeText: 'text-rose-100',
    icon: Bug,
  },
  aces: {
    color: 'text-amber-200',
    bg: 'bg-amber-500/10',
    border: 'border-amber-400/25',
    badgeBg: 'bg-amber-500/15',
    badgeText: 'text-amber-100',
    icon: AceNavIcon,
  },
};

function SectionPanel({ section }: { section: ChangelogSection }) {
  const [isOpen, setIsOpen] = useState(true);
  const cfg = sectionConfig[section.type];
  const Icon = cfg.icon;

  return (
    <div className={cn('overflow-hidden rounded-2xl border', cfg.border, cfg.bg)}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-4 transition-colors hover:bg-white/[0.04]"
      >
        <div className="flex items-center gap-3">
          <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', cfg.badgeBg)}>
            <Icon className={cn('h-4 w-4', cfg.color)} />
          </div>
          <span className={cn('font-poppins text-sm font-bold', cfg.color)}>{section.label}</span>
          <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', cfg.badgeBg, cfg.badgeText)}>
            {section.count}
          </span>
        </div>
        <ChevronDown
          className={cn('h-4 w-4 text-blue-100/50 transition-transform duration-300', isOpen && 'rotate-180')}
        />
      </button>

      <div
        className={cn(
          'grid transition-all duration-300 ease-in-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <ul className="space-y-4 border-t border-white/[0.06] bg-[#05051f]/40 px-5 py-4">
            {section.items.map((item, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400/70" />
                <div>
                  <p className="font-inter text-sm font-semibold text-white">{item.title}</p>
                  {item.details && item.details.length > 0 ? (
                    <ul className="mt-1.5 space-y-1">
                      {item.details.map((d, j) => (
                        <li key={j} className="flex gap-2 font-inter text-sm text-blue-100/75">
                          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-white/25" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export function PainelChangelogVersionDetailPage({
  product,
  version,
}: {
  product: ChangelogProductId;
  version: string;
}) {
  const { sources, loading } = useChangelogSources();
  const source = sources[product];
  const entry = useMemo(() => {
    return source.entries.find((e) => e.version === version);
  }, [source.entries, version]);

  if (loading && !entry) {
    return (
      <ChangelogPageShell className="space-y-6 pb-10">
        <ChangelogProductTabs product={product} />
        <div className={cn(painelSurfaceClass, 'p-8')}>
          <p className="font-inter text-sm text-slate-400">A carregar versão…</p>
        </div>
      </ChangelogPageShell>
    );
  }

  if (!entry) {
    return (
      <ChangelogPageShell className="space-y-6 pb-10">
        <ChangelogProductTabs product={product} />
        <Button variant="ghost" asChild className={painelPageBackButtonClass}>
          <Link href={`/painel/changelog/${product}/versions`}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Versões
          </Link>
        </Button>
        <div className={cn(painelSurfaceClass, 'p-8')}>
          <h1 className="font-poppins text-xl font-bold text-white">Versão não encontrada</h1>
          <p className="mt-2 font-inter text-sm text-slate-400">
            Não há changelog de {source.label} com a versão &ldquo;{version}&rdquo; na base atual.
          </p>
          <Button variant="ghost" asChild className={cn(painelPageBackButtonClass, 'mt-6 mb-0 ml-0')}>
            <Link href={`/painel/changelog/${product}/versions`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Versões
            </Link>
          </Button>
        </div>
      </ChangelogPageShell>
    );
  }

  return (
    <ChangelogPageShell className="space-y-8 pb-10">
      <ChangelogProductTabs product={product} />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <Button variant="ghost" asChild className={painelPageBackButtonClass}>
            <Link href={`/painel/changelog/${product}/versions`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Versões
            </Link>
          </Button>
          <p className="font-inter text-[10px] font-bold uppercase tracking-[0.2em] text-sky-300/55">
            Changelog · {source.label}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">v{entry.version}</h1>
            {entry.latest ? (
              <span className="rounded-full bg-sky-500/20 px-2.5 py-1 font-poppins text-[10px] font-bold uppercase tracking-wider text-sky-100 ring-1 ring-sky-400/30">
                Versão atual
              </span>
            ) : null}
            <span className="font-inter text-sm text-slate-400">{entry.date}</span>
          </div>
          <p className="mt-2 max-w-2xl font-inter text-sm text-slate-400">
            Conteúdo alinhado à{' '}
            <Link href={changelogPublicHref(product)} className="text-sky-300 underline-offset-2 hover:text-sky-200 hover:underline">
              página pública de Changelogs
            </Link>
            .
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:shrink-0">
          <Button variant="ghost" asChild className={painelGhostNavButtonClass}>
            <Link href={changelogPublicHref(product)} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              Ver no site
            </Link>
          </Button>
          <Button asChild className="rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 font-poppins font-semibold">
            <Link href={`${changelogVersionHref(product, entry.version)}/edit`}>
              <Pencil className="mr-2 h-4 w-4" />
              Editar no painel
            </Link>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-white/10 pb-4">
        {entry.sections.map((s, i) => {
          const cfg = sectionConfig[s.type];
          const Icon = cfg.icon;
          return (
            <span
              key={`${i}-${s.type}`}
              className={cn(
                'inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 font-poppins text-xs font-semibold shadow-sm',
                cfg.border,
                cfg.badgeBg,
                cfg.badgeText
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              <span className="min-w-0 truncate sm:whitespace-normal">{s.label}</span>
              <span
                className="shrink-0 tabular-nums rounded-md bg-black/25 px-2 py-0.5 text-[11px] font-bold text-white/95"
                title={`${s.count} itens`}
              >
                {s.count}
              </span>
            </span>
          );
        })}
      </div>

      <div className="space-y-4">
        {entry.sections.map((section) => (
          <SectionPanel key={section.type} section={section} />
        ))}
      </div>

      <p className="font-inter text-[11px] text-slate-500">
        O conteúdo acima corresponde ao registro desta versão no changelog público (
        <Link href={changelogPublicHref(product)} className="text-sky-300 hover:underline">
          /changelog
        </Link>
        ).
      </p>
    </ChangelogPageShell>
  );
}
